import React, { useEffect, useState } from 'react'
import { createThread, listMessages, listThreads, sendMessage, markThreadRead, ThreadSummary, MessageOut } from '../services/messageService'

const Messages: React.FC = () => {
  const [threads, setThreads] = useState<ThreadSummary[]>([])
  const [activeThread, setActiveThread] = useState<ThreadSummary | null>(null)
  const [messages, setMessages] = useState<MessageOut[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [newThreadSubject, setNewThreadSubject] = useState('')
  const [newThreadParticipants, setNewThreadParticipants] = useState('')
  const [loading, setLoading] = useState(true)

  const tenant = localStorage.getItem('currentTenant')
  const user = localStorage.getItem('user')
  const tenantId = tenant ? JSON.parse(tenant).id : user ? JSON.parse(user).tenant_id : ''

  const loadThreads = async () => {
    setLoading(true)
    try {
      const data = await listThreads(tenantId)
      setThreads(data)
      if (!activeThread && data.length > 0) {
        setActiveThread(data[0])
      }
    } finally {
      setLoading(false)
    }
  }

  const loadMessages = async (threadId: string) => {
    const data = await listMessages(tenantId, threadId)
    setMessages(data)
  }

  useEffect(() => {
    loadThreads()
  }, [])

  useEffect(() => {
    if (activeThread) {
      loadMessages(activeThread.id)
      markThreadRead(tenantId, activeThread.id)
    }
  }, [activeThread])

  const handleSend = async () => {
    if (!activeThread || !newMessage.trim()) return
    const msg = await sendMessage(tenantId, activeThread.id, newMessage.trim())
    setMessages((prev) => [...prev, msg])
    setNewMessage('')
  }

  const handleCreateThread = async () => {
    const participants = newThreadParticipants
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
    if (participants.length === 0) return
    const thread = await createThread(tenantId, newThreadSubject.trim() || undefined, participants)
    setThreads((prev) => [thread, ...prev])
    setActiveThread(thread)
    setNewThreadSubject('')
    setNewThreadParticipants('')
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h2 className="text-lg font-semibold mb-4">Threads</h2>
          <div className="space-y-2 mb-6">
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Subject"
              value={newThreadSubject}
              onChange={(e) => setNewThreadSubject(e.target.value)}
            />
            <input
              className="w-full rounded border px-3 py-2 text-sm"
              placeholder="Participant IDs (comma-separated)"
              value={newThreadParticipants}
              onChange={(e) => setNewThreadParticipants(e.target.value)}
            />
            <button className="w-full btn-primary" onClick={handleCreateThread}>
              Start Thread
            </button>
          </div>
          {loading ? (
            <div className="text-sm text-gray-500">Loading threads...</div>
          ) : (
            <ul className="space-y-2">
              {threads.map((thread) => (
                <li
                  key={thread.id}
                  className={`rounded border px-3 py-2 text-sm cursor-pointer ${
                    activeThread?.id === thread.id ? 'bg-green-50 border-green-300' : 'bg-white'
                  }`}
                  onClick={() => setActiveThread(thread)}
                >
                  <div className="font-medium">{thread.subject || 'No subject'}</div>
                  <div className="text-xs text-gray-500">{thread.participant_ids.join(', ')}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="md:col-span-2 bg-white rounded-lg shadow p-4 flex flex-col h-[75vh]">
          <h2 className="text-lg font-semibold mb-4">Messages</h2>
          <div className="flex-1 overflow-y-auto border rounded p-3 space-y-3">
            {activeThread ? (
              messages.map((msg) => (
                <div key={msg.id} className="text-sm">
                  <div className="text-xs text-gray-500">{msg.sender_id}</div>
                  <div className="rounded bg-gray-100 px-3 py-2">{msg.body}</div>
                </div>
              ))
            ) : (
              <div className="text-sm text-gray-500">Select a thread to view messages.</div>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              className="flex-1 rounded border px-3 py-2 text-sm"
              placeholder="Type a message..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <button className="btn-primary" onClick={handleSend}>
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Messages
